import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, BriefcaseBusiness, Check, CheckCircle2, GraduationCap, Layers3, LockKeyhole, ShieldCheck } from "lucide-react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "universal-cookie";
import { apiFetch } from "../../commons/axiosReq";
import "./Checkout.css";

const scopeInfo = {
  Study: { title: "مسیر تحصیلی", description: "دانشگاه‌ها، استادها، قالب‌های مکاتبه و مدیریت اپلای", icon: GraduationCap },
  Job: { title: "مسیر شغلی", description: "فرصت‌های شغلی، کارفرماها، رزومه و پیگیری درخواست", icon: BriefcaseBusiness },
  Both: { title: "بستهٔ کامل تحصیلی + شغلی", description: "دسترسی هم‌زمان به هر دو مسیر با ۱۵٪ تخفیف ترکیبی", icon: Layers3 },
};
const toNumber = (value) => Number(String(value ?? "").replace(/,/g, ""));
const format = (value) => new Intl.NumberFormat("fa-IR").format(value);

const Checkout = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const requestedScope = searchParams.get("scope");
  const scope = scopeInfo[requestedScope] ? requestedScope : "Study";
  const planId = Number(searchParams.get("planId")) || 0;
  const [plan, setPlan] = useState(location.state?.plan || null);
  const [loading, setLoading] = useState(!plan);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const token = new Cookies().get("token");

  useEffect(() => {
    let active = true;
    apiFetch("plan/GetHomePlans", "get").then((result) => {
      if (!active) return;
      const found = Array.isArray(result.data) ? result.data.find((item) => Number(item.planId) === planId) : null;
      if (found) setPlan(found);
      setLoading(false);
    });
    return () => { active = false; };
  }, [planId]);

  const pricing = useMemo(() => {
    const base = toNumber(plan?.cost);
    const planDiscount = Math.min(Math.max(Number(plan?.discount) || 0, 0), 100);
    const single = base ? Math.round(base * (100 - planDiscount) / 100) : 0;
    const beforeBundle = scope === "Both" ? single * 2 : single;
    const total = scope === "Both" ? Math.round(beforeBundle * 0.85) : beforeBundle;
    return { beforeBundle, total, unit: base > 0 && base < 10000 ? "میلیون تومان" : "تومان" };
  }, [plan, scope]);
  const currentPath = `/checkout?planId=${planId}&scope=${scope}`;
  const ScopeIcon = scopeInfo[scope].icon;
  const recipientSummary = scope === "Study"
    ? `${format(plan?.teacherNumber || 0)} استاد و مخاطب دانشگاهی در هر برنامه`
    : scope === "Job"
      ? `${format(plan?.teacherNumber || 0)} موقعیت شغلی دارای ایمیل تماس در هر برنامه`
      : `${format(plan?.teacherNumber || 0)} مخاطب در هر مسیر`;

  const activate = async () => {
    if (!token) {
      navigate(`/SignUp?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }
    if (!planId) {
      setError("اطلاعات این پلن هنوز از سرور دریافت نشده است. لطفاً از صفحهٔ پلن‌ها دوباره انتخاب کنید.");
      return;
    }
    setSubmitting(true);
    setError("");
    const result = await apiFetch("plan/ActivateSubscription", "post", { planId, scope });
    if (result.ok) setDone(true);
    else setError(result.error || "فعال‌سازی پلن انجام نشد. لطفاً دوباره تلاش کنید.");
    setSubmitting(false);
  };

  if (done) return <main className="checkout-page" dir="rtl"><section className="checkout-success"><span><CheckCircle2 size={42} /></span><h1>اشتراک شما فعال شد</h1><p>پلن «{plan?.title}» برای {scopeInfo[scope].title} با اعتبار ۳۰ روزه روی حساب شما فعال است.</p><Link to="/user/dashboard">رفتن به پنل کاربری <ArrowLeft size={18} /></Link><small>این مرحله فعلاً آزمایشی است و هیچ پرداختی انجام نشد.</small></section></main>;

  return <main className="checkout-page" dir="rtl"><div className="checkout-shell">
    <header className="checkout-head"><div><span>تکمیل انتخاب پلن</span><h1>جمع‌بندی و فعال‌سازی اشتراک</h1><p>جزئیات را بررسی کنید؛ در نسخهٔ فعلی بدون ورود به درگاه، پلن مستقیماً فعال می‌شود.</p></div><Link to="/#pricing">تغییر پلن</Link></header>
    <div className="checkout-grid"><section className="checkout-main-card">
      <div className="checkout-path"><span><ScopeIcon size={26} /></span><div><small>نوع اشتراک</small><h2>{scopeInfo[scope].title}</h2><p>{scopeInfo[scope].description}</p></div>{scope === "Both" && <b>۱۵٪ تخفیف ترکیبی</b>}</div>
      <div className="checkout-plan"><div><small>پلن انتخابی</small><h3>{loading ? "در حال دریافت..." : plan?.title || "پلن انتخاب نشده"}</h3><p>{plan?.description}</p></div><span>۳۰ روز اعتبار</span></div>
      <ul className="checkout-features"><li><Check size={17} /> ارسال به {recipientSummary}</li><li><Check size={17} /> فرصت‌های منتخب از {format(plan?.countryNumber || 0)} کشور</li><li><Check size={17} /> ارسال و پیگیری خودکار درخواست‌ها</li>{scope === "Both" && <li><Check size={17} /> دسترسی کامل به ابزارهای هر دو مسیر</li>}</ul>
      <div className="checkout-notice"><ShieldCheck size={20} /><div><strong>فعلاً بدون درگاه پرداخت</strong><p>با زدن دکمهٔ فعال‌سازی، اشتراک آزمایشی مستقیم روی حساب شما ثبت می‌شود و مبلغی کسر نخواهد شد.</p></div></div>
    </section><aside className="checkout-summary"><h2>خلاصه سفارش</h2><dl><div><dt>پلن</dt><dd>{plan?.title || "—"}</dd></div><div><dt>مسیر</dt><dd>{scopeInfo[scope].title}</dd></div>{scope === "Both" && <><div><dt>قیمت دو مسیر</dt><dd>{pricing.total ? `${format(pricing.beforeBundle)} ${pricing.unit}` : "پس از استعلام"}</dd></div><div className="is-discount"><dt>تخفیف ترکیبی</dt><dd>۱۵٪</dd></div></>}<div className="checkout-total"><dt>مبلغ نهایی</dt><dd>{pricing.total ? <><strong>{format(pricing.total)}</strong><span>{pricing.unit}</span></> : "ثبت آزمایشی"}</dd></div></dl>
      {error && <p className="checkout-error" role="alert">{error}</p>}
      <button type="button" onClick={activate} disabled={submitting || loading}>{submitting ? "در حال فعال‌سازی..." : token ? "فعال‌سازی اشتراک" : "ورود و ادامه"}<LockKeyhole size={17} /></button>
      {!token && <p className="checkout-auth-hint">حساب دارید؟ <Link to={`/SignIn?redirect=${encodeURIComponent(currentPath)}`}>وارد شوید</Link></p>}
      <small className="checkout-safe">بدون پرداخت و بدون نیاز به اطلاعات بانکی</small>
    </aside></div>
  </div></main>;
};

export default Checkout;
